import React, { useEffect, useState, useCallback, useMemo } from "react";

import A5PaymentVoucher from './paymentVoucherA5';
import Navbar from './components/navbar';
import { ErrorAlert, SuccessAlert } from './components/alertBox';
import { useLocation } from "react-router";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Banknote } from "lucide-react";

const EditPaymentVoucher = () => {
    const { state } = useLocation(); // Fixed: Added parentheses and use state

    const [errors, setErrors] = useState({});

    const navigate = useNavigate()
    const voucherDoc = useMemo(() => {
        return state?.voucherDoc || {};
    }, [state?.voucherDoc]);

    const [showVoucher, setShowVoucher] = useState(false);
    const [savedVoucherData, setSavedVoucherData] = useState(null);

    // Added formData state to manage editable fields
    const [formData, setFormData] = useState({
        date: '',
        voucherNo: '',
        payableTo: '',
        chequeDetails: '',
        poNo: '',
        grnNo: '',
        items: [{ description: '', amount: '', rs: '', cts: '' }],
        totalRs: '',
        totalCts: '',
        name: '',
        transferMethod: ''
    });


    const [saveIsLoading, setSaveIsLoading] = useState(false);

    const [isTransferMethodOpen, setIsTransferMethodOpen] = useState(false);
    const TRANSFER_METHODS = [
        { value: 'peoples_bank', label: "People's Bank" },
        { value: 'seylan_bank', label: 'Seylan Bank' },
        { value: 'cash', label: 'Cash' }
    ];

    const fetchVoucherData = useCallback(async (id) => {
        try {
            const response = await fetch(`https://payvoucher.netgateerp.lk/api/users/payment-vouchers/${id}`);
            const resData = await response.json();
            if (!response.ok) {
                console.log("Error retrieving voucher data from backend", response.status, response.statusText);
                return;
            }
            console.log("Voucher data based on id:", resData.data);
            setFormData({
                poNo: resData.data?.poNo || '',
                grnNo: resData.data?.grnNo || '',
                date: timestampToCalenderDate(resData.data?.date) || '',
                chequeDetails: resData.data?.chequeDetails || '',
                payableTo: resData.data?.payableTo || '',
                voucherNo: resData.data?.voucherNo || '',
                transferMethod: resData.data?.transferMethod || '',
                items: resData.data?.items,
                totalRs: resData.data?.totalRs
            });
        } catch (e) {
            console.log("Error fetching voucher data", e);
        }
    }, [setFormData]); // Include setFormData as dependency

    useEffect(() => {
        // Use memoizedVoucherDoc instead of voucherDoc
        console.log("Voucher doc id:", voucherDoc);
        if (voucherDoc) {
            fetchVoucherData(voucherDoc);
        }
    }, [voucherDoc, fetchVoucherData]);

    useEffect(() => {
        const userStr = localStorage.getItem('user')
        const userData = JSON.parse(userStr)
        const userName = userData.name
        setFormData(prev => ({ ...prev, name: userName }))
    }, [])


    const handleInputChange = async (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        // Clear specific error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    // Added missing formatNumber function
    const formatNumber = (num) => {
        return num ? parseFloat(num).toFixed(2) : '0.00';
    };

    // Added missing handleSave function
    const handleSave = async () => {
        setSaveIsLoading(true);
        try {
            // Here you would typically save the updated PO and GRN numbers
            // For now, just simulate the save
            const updatedVoucherData = {

                poNo: formData.poNo,
                grnNo: formData.grnNo,
                chequeDetails: formData.chequeDetails,
                transferMethod: formData.transferMethod,
            };

            const response = await fetch(`https://payvoucher.netgateerp.lk/api/users/update-payment-voucher/${voucherDoc}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedVoucherData)
            })
            // const res = response.json()
            if (!response.ok) {
                console.log("Error in updating the data", response.status, response.statusText)
                ErrorAlert("Error in updating the data")
                return
            }

            SuccessAlert("Successfully updated the payment voucher")
            setSavedVoucherData({ ...formData })
            setShowVoucher(true)
            setSaveIsLoading(false)
        } catch (error) {
            console.error('Save error:', error);
            setSaveIsLoading(false)
        } finally {
            setSaveIsLoading(false);
        }
    };

    // Added clear function for editable fields only
    const clearEditableFields = () => {
        setFormData(prevData => ({
            ...prevData,
            poNo: '',
            grnNo: ''
        }));
    };
    const formatCurrency = (value) => {
        if (!value || isNaN(value)) return '0.00';

        const number = parseFloat(value);
        return number.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const timestampToCalenderDate = (timestamp) => {
        const date = new Date(timestamp);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        return `${year}-${month}-${day}`;;
    };

    return (
        <>
            <div className="w-full min-h-screen bg-gray-50 flex flex-col overflow-x-hidden">
                <Navbar />
                <div className="flex-1 p-4 overflow-x-hidden">
                    <div className="border-2 border-blue-800 min-h-full bg-white shadow-lg max-w-full">
                        {/* Header */}
                        <div className="border-b-2 border-blue-800 p-4 sm:p-8">
                            <div className="flex flex-col sm:flex-row items-start">
                                <div className="w-24 h-24 sm:w-32 sm:h-32 flex items-center justify-center mb-4 sm:mb-0 sm:mr-8 mx-auto sm:mx-0">
                                    <img
                                        src="/netgatelogo.png"
                                        alt="NetGate Lanka Logo"
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                                <div className="text-blue-800 text-center flex-1 min-w-0">
                                    <div className="font-bold text-xl sm:text-3xl mb-3 leading-tight">NetGate Lanka (Pvt) Ltd.</div>
                                    <div className="text-sm sm:text-lg mb-2 leading-relaxed">No. 251/1A, Moratuwa Road, Piliyandala.</div>
                                    <div className="text-sm sm:text-lg mb-6 leading-relaxed">Tel: 070 166 6777</div>
                                    <div className="border-2 border-blue-800 inline-block px-4 sm:px-8 py-3 mt-2">
                                        <div className="font-semibold text-lg sm:text-xl">PAYMENT VOUCHER</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Form Fields */}
                        <div className="p-4 sm:p-8 overflow-x-hidden">
                            {/* Top row - READ ONLY */}
                            <div className="flex flex-col sm:flex-row sm:justify-between mb-8 gap-4">
                                <div className="flex items-center">
                                    <span className="text-sm mr-4 text-blue-800 font-medium min-w-[50px]">Date :</span>
                                    <input
                                        type="date"
                                        value={formData.date || ''}
                                        readOnly
                                        className="border-b-2 border-dotted border-gray-400 bg-gray-100 cursor-not-allowed px-2 py-2 w-36 sm:w-44 text-sm text-gray-600"
                                        title="This field is read-only"
                                    />
                                </div>
                                <div className="flex items-center">
                                    <span className="text-sm mr-4 text-blue-800 font-medium">No.</span>
                                    <input
                                        type="text"
                                        value={formData.voucherNo || ''}
                                        readOnly
                                        className="border-b-2 border-dotted border-gray-400 bg-gray-100 cursor-not-allowed px-2 py-2 w-24 font-bold text-sm text-gray-600"
                                        title="Auto-generated voucher number (read-only)"
                                    />
                                </div>
                            </div>

                            {/* Second row - READ ONLY */}
                            <div className="mb-8">
                                <div className="flex items-center relative">
                                    <span className="text-sm mr-4 text-blue-800 font-medium min-w-[100px]">Payable to :</span>
                                    <div className="flex-1 ml-3 relative">
                                        <input
                                            type="text"
                                            value={formData.payableTo || ''}
                                            readOnly
                                            className="border-b-2 border-dotted border-gray-400 bg-gray-100 cursor-not-allowed px-2 py-2 w-full text-sm min-w-0 text-gray-600"
                                            title="This field is read-only"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Third row - Transfer Method / Cheque Details */}
                            <div className="mb-8 overflow-x-visible flex flex-col sm:flex-row gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center relative">
                                        <span className="text-sm mr-4 text-blue-800 font-medium min-w-[140px] sm:min-w-[150px]">Transfer Method :</span>
                                        <div className="flex-1 ml-3 relative">
                                            <div
                                                className={`border-b-2 border-dotted border-blue-600 focus:border-blue-600 px-2 py-2 w-full text-sm min-w-0 pr-8 bg-yellow-50 cursor-pointer flex items-center ${formData.transferMethod ? 'text-black' : 'text-gray-500'}`}
                                                onClick={() => setIsTransferMethodOpen(!isTransferMethodOpen)}
                                                title="This field is editable"
                                            >
                                                {formData.transferMethod ? (
                                                    <div className="flex items-center">
                                                        {formData.transferMethod === "peoples_bank" && (
                                                            <img src="/peoples_bank.png" alt="People's Bank" className="w-5 h-5 mr-2 object-contain" />
                                                        )}
                                                        {formData.transferMethod === "seylan_bank" && (
                                                            <img src="/seylan_bank.png" alt="Seylan Bank" className="w-5 h-5 mr-2 object-contain" />
                                                        )}
                                                        {formData.transferMethod === "cash" && (
                                                            <Banknote className="w-5 h-5 mr-2 text-green-600" />
                                                        )}
                                                        <span>
                                                            {TRANSFER_METHODS.find(m => m.value === formData.transferMethod)?.label || formData.transferMethod}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    "Select transfer method"
                                                )}
                                            </div>
                                            <div
                                                className="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer"
                                                onClick={() => setIsTransferMethodOpen(!isTransferMethodOpen)}
                                            >
                                                <ChevronDown className={`w-4 h-4 text-blue-600 transition-transform ${isTransferMethodOpen ? 'rotate-180' : ''}`} />
                                            </div>

                                            {isTransferMethodOpen && (
                                                <div
                                                    className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
                                                    style={{
                                                        top: '100%',
                                                        left: 0,
                                                        right: 0,
                                                        minWidth: '100%'
                                                    }}
                                                >
                                                    {TRANSFER_METHODS.map((method) => (
                                                        <div
                                                            key={method.value}
                                                            className="px-3 py-2 cursor-pointer text-sm flex items-center hover:bg-blue-50"
                                                            onClick={() => {
                                                                handleInputChange('transferMethod', method.value);
                                                                setIsTransferMethodOpen(false);
                                                            }}
                                                        >
                                                            {method.value === "peoples_bank" && (
                                                                <img src="/peoples_bank.png" alt="People's Bank" className="w-5 h-5 mr-3 object-contain" />
                                                            )}
                                                            {method.value === "seylan_bank" && (
                                                                <img src="/seylan_bank.png" alt="Seylan Bank" className="w-5 h-5 mr-3 object-contain" />
                                                            )}
                                                            {method.value === "cash" && (
                                                                <Banknote className="w-5 h-5 mr-3 text-green-600" />
                                                            )}
                                                            <span>{method.label}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {errors.transferMethod && (
                                        <div className="text-red-500 text-xs mt-2 ml-[150px] sm:ml-[160px]">{errors.transferMethod}</div>
                                    )}
                                </div>
                                <div className="flex-1 flex items-center">
                                    <span className="text-sm mr-4 text-blue-800 font-medium min-w-[140px] sm:min-w-[180px]">Cheque/Cash/Online :</span>
                                    <input
                                        type="text"
                                        value={formData.chequeDetails || ''}
                                        onChange={(e) => handleInputChange('chequeDetails', e.target.value)}
                                        className="border-b-2 border-dotted border-blue-600 px-2 py-2 flex-1 ml-3 text-sm min-w-0 text-gray-600 bg-yellow-50"
                                        placeholder="Enter Cheque No./Cash/Online details"
                                        title="This field is editable"
                                    />
                                </div>
                            </div>

                            {/* Fourth row - EDITABLE PO and GRN fields */}
                            <div className="flex flex-col sm:flex-row sm:justify-between mb-10 gap-4">
                                <div className="flex items-center">
                                    <span className="text-sm mr-4 text-blue-800 font-medium">P.O. No.</span>
                                    <input
                                        type="text"
                                        value={formData.poNo}
                                        onChange={(e) => handleInputChange('poNo', e.target.value)}
                                        className="border-b-2 border-dotted border-blue-600 focus:border-blue-600 outline-none px-2 py-2 w-32 sm:w-36 ml-3 text-sm bg-yellow-50"
                                        placeholder="P.O. Number"
                                        title="This field is editable"
                                    />
                                </div>
                                <div className="flex items-center">
                                    <span className="text-sm mr-4 text-blue-800 font-medium">G.R.N. No.</span>
                                    <input
                                        type="text"
                                        value={formData.grnNo}
                                        onChange={(e) => handleInputChange('grnNo', e.target.value)}
                                        className="border-b-2 border-dotted border-blue-600 focus:border-blue-600 outline-none px-2 py-2 w-32 sm:w-36 ml-3 text-sm bg-yellow-50"
                                        placeholder="G.R.N. Number"
                                        title="This field is editable"
                                    />
                                </div>
                            </div>

                            {/* Table - READ ONLY */}
                            <div className="border-2 border-blue-800 mb-10 shadow-sm overflow-x-auto">
                                <div className="min-w-full">
                                    {/* Table header */}
                                    <div className="text-center text-sm font-semibold bg-blue-50">
                                        <div className="flex border-b-2 border-blue-800 min-w-[500px]">
                                            <div className="flex-1 border-r-2 border-blue-800 py-4 min-w-[300px]">
                                                Description
                                            </div>
                                            <div className="w-48 sm:w-64">
                                                <div className="py-2 border-b border-blue-800">
                                                    Amount
                                                </div>
                                                <div className="flex">
                                                    <div className="flex-1 py-2 border-r-2 border-blue-800">
                                                        Rs
                                                    </div>
                                                    <div className="flex-1 py-2">
                                                        Cts
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Table content */}
                                    <div className="min-h-48 flex flex-col min-w-[500px]">
                                        {/* Data rows - READ ONLY */}
                                        {(formData.items || []).map((item, index) => (
                                            <div key={index} className="flex border-b border-blue-800 bg-gray-50">
                                                <div className="flex-1 border-r-2 border-blue-800 p-4 min-w-[300px]">
                                                    <textarea
                                                        value={item.description || ''}
                                                        readOnly
                                                        className="w-full outline-none bg-transparent text-sm resize-none text-gray-600 cursor-not-allowed"
                                                        rows={2}
                                                        style={{ minHeight: '40px' }}
                                                        title="This field is read-only"
                                                    />
                                                </div>
                                                <div className="w-48 sm:w-64 flex">
                                                    <div className="flex-1 border-r-2 border-blue-800 p-4">
                                                        <input
                                                            type="text"
                                                            value={formatCurrency(item.rs) || '0.00'}
                                                            readOnly
                                                            className="w-full outline-none py-2 text-center bg-transparent text-sm text-gray-600 cursor-not-allowed"
                                                            title="This field is read-only"
                                                        />
                                                    </div>
                                                    <div className="flex-1 p-4 flex items-center">
                                                        <input
                                                            type="number"
                                                            value={item.cts || ''}
                                                            readOnly
                                                            className="w-full outline-none py-2 text-center bg-transparent text-sm text-gray-600 cursor-not-allowed"
                                                            title="This field is read-only"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        {/* Empty space filler */}
                                        <div className="flex flex-1 bg-gray-50">
                                            <div className="flex-1 border-r-2 border-blue-800 min-w-[300px]"></div>
                                            <div className="w-48 sm:w-64 flex">
                                                <div className="flex-1 border-r-2 border-blue-800"></div>
                                                <div className="flex-1"></div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Total row - READ ONLY */}
                                    <div className="border-t-2 border-blue-800 text-center text-sm font-bold bg-blue-100 min-w-[500px]">
                                        <div className="flex">
                                            <div className="flex-1 border-r-2 border-blue-800 py-4 text-blue-800 min-w-[300px]">
                                                Total
                                            </div>
                                            <div className="w-48 sm:w-64 flex text-blue-800">
                                                <div className="flex-1 border-r-2 border-blue-800 py-4">
                                                    {formatCurrency(formData.totalRs)}
                                                </div>
                                                <div className="flex-1 py-4">
                                                    {formatNumber(formData.totalCts)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>



                            {/* Action buttons */}
                            <div className="flex flex-col sm:flex-row justify-end gap-4 sm:gap-6 mt-12 pt-6">
                                <button
                                    onClick={handleSave}
                                    disabled={saveIsLoading}
                                    className="px-8 py-3 bg-[#1B2F69] text-white rounded-lg hover:bg-blue-800 transition-colors font-medium shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {saveIsLoading ? 'Saving...' : 'Save Changes'}
                                </button>
                                <button
                                    onClick={clearEditableFields}
                                    className="px-8 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium shadow-md"
                                >
                                    Clear Editable Fields
                                </button>
                            </div>

                            {/* Info message */}
                            <div className="mt-4 p-3 bg-blue-50 border-l-4 border-blue-500 text-sm text-blue-700">
                                <strong>Note:</strong> Only P.O. No. and G.R.N. No. fields are editable. All other fields are read-only and display the original voucher data.
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Show ViewPaymentVoucher component when saved */}
            {showVoucher && savedVoucherData && (
                <A5PaymentVoucher
                    voucherData={savedVoucherData}
                    onClose={() => {
                        setShowVoucher(false);
                        navigate('/home')
                    }}
                />
            )}
        </>
    )
}

export default EditPaymentVoucher;